import axios, { CancelTokenSource } from 'axios'
import { useCallback, useEffect, useRef } from 'react'

import { getPatientDetail } from '../../../services/ApiService'
import { PatientSummaryDto } from '../../../types/swaggerTypes'
import { resolvePtaPreOrderStatus } from '../PatientDetail/ptaBeginOrderGate'
import {
  PTA_CONSENT_SYNC_MAX_DURATION_MS,
  PTA_CONSENT_SYNC_POLL_INTERVAL_MS
} from '../PatientDetail/usePtaPatientDetailsRefreshOnReturn'
import {
  normalizePatientOrderStatus,
  PatientOrderStatus
} from './Patient.model'
import { isPatientRowRestrictedForCaller } from './patientRowAccess'
import { isPostTrialAccessPatientRow } from './ptaActionVisibility'

export const isPtaDashboardRefreshEligible = (
  patient: PatientSummaryDto,
  programDetailCountryProgramStatus?: string
): boolean =>
  isPostTrialAccessPatientRow(patient, programDetailCountryProgramStatus) &&
  !patient.stockOrderFlag &&
  !isPatientRowRestrictedForCaller(patient) &&
  patient.patientId > 0 &&
  Number(patient.physicianId) > 0 &&
  [
    PatientOrderStatus.AwaitingConsent,
    PatientOrderStatus.AwaitingBaselineData
  ].includes(
    normalizePatientOrderStatus(
      patient.patientOrderStatus
    ) as PatientOrderStatus
  )

interface RefreshParams {
  patients?: PatientSummaryDto[]
  isLoading: boolean
  scopeKey: string
  programDetailCountryProgramStatus?: string
  onStatusChanged: (patient: PatientSummaryDto, status: string) => void
}

interface SyncTarget {
  patientId: number
  physicianId: string
  status: string
  deadline?: number
}

/** Read only the actionable PTA patient who opened Castor, after the user returns. */
export function usePtaDashboardRefreshOnReturn(
  params: RefreshParams
): (patient: PatientSummaryDto) => void {
  const latest = useRef(params)
  latest.current = params
  const target = useRef<SyncTarget>()
  const request = useRef<CancelTokenSource>()
  const retryTimer = useRef<ReturnType<typeof setTimeout>>()
  const deadlineTimer = useRef<ReturnType<typeof setTimeout>>()

  const pause = useCallback(() => {
    clearTimeout(retryTimer.current)
    clearTimeout(deadlineTimer.current)
    retryTimer.current = undefined
    deadlineTimer.current = undefined
    const pendingRequest = request.current
    request.current = undefined
    pendingRequest?.cancel()
  }, [])

  const stop = useCallback(() => {
    target.current = undefined
    pause()
  }, [pause])

  const trackCastorPatient = useCallback(
    (patient: PatientSummaryDto) => {
      if (
        !isPtaDashboardRefreshEligible(
          patient,
          latest.current.programDetailCountryProgramStatus
        )
      ) {
        return
      }
      stop()
      target.current = {
        patientId: patient.patientId,
        physicianId: patient.physicianId,
        status: normalizePatientOrderStatus(patient.patientOrderStatus)
      }
    },
    [stop]
  )

  useEffect(() => {
    const currentRow = (session: SyncTarget) => {
      const row = latest.current.patients?.find(
        (patient) =>
          patient.patientId === session.patientId &&
          patient.physicianId === session.physicianId
      )
      return row &&
        isPtaDashboardRefreshEligible(
          row,
          latest.current.programDetailCountryProgramStatus
        ) &&
        normalizePatientOrderStatus(row.patientOrderStatus) === session.status
        ? row
        : undefined
    }

    const resume = async () => {
      const session = target.current
      if (
        !session ||
        document.visibilityState !== 'visible' ||
        request.current ||
        retryTimer.current
      ) {
        return
      }
      const row = currentRow(session)
      if (!row) {
        stop()
        return
      }

      session.deadline ??= Date.now() + PTA_CONSENT_SYNC_MAX_DURATION_MS
      const remaining = session.deadline - Date.now()
      if (remaining <= 0) {
        stop()
        return
      }
      deadlineTimer.current ??= setTimeout(stop, remaining)

      const retry = () => {
        retryTimer.current = setTimeout(() => {
          retryTimer.current = undefined
          void resume()
        }, PTA_CONSENT_SYNC_POLL_INTERVAL_MS)
      }
      if (latest.current.isLoading) {
        retry()
        return
      }

      const source = axios.CancelToken.source()
      request.current = source
      try {
        const response = await getPatientDetail(
          session.patientId.toString(),
          session.physicianId,
          source.token
        )
        if (
          target.current !== session ||
          request.current !== source ||
          !currentRow(session)
        ) {
          return
        }
        const status = normalizePatientOrderStatus(
          resolvePtaPreOrderStatus(response.data, true)
        )
        if (status && status !== session.status) {
          stop()
          latest.current.onStatusChanged(row, status)
        } else if (session.status === PatientOrderStatus.AwaitingConsent) {
          retry()
        } else {
          // Screening is explicitly verified and saved through the confirmation flow.
          stop()
        }
      } catch {
        // Keep the cached status on failure; ordering still uses the BFF guards.
        if (request.current === source) stop()
      } finally {
        if (request.current === source) request.current = undefined
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void resume()
      } else {
        pause()
      }
    }
    const handleFocus = () => {
      void resume()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      stop()
    }
  }, [params.scopeKey, pause, stop])

  return trackCastorPatient
}
